import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Typography,
  Upload,
  message,
} from "antd";
import { CopyOutlined, UploadOutlined, UserOutlined } from "@ant-design/icons";
import { uploadFile } from "../../../helper/firebaseUpload";

const MAX_IMAGE_MB = 5;

const EditRoleModal = ({
  visible,
  isLoading,
  user,
  onCancel,
  onSubmit,
  lockRole = false,
}) => {
  const [form] = Form.useForm();
  // The newly picked File (null until the admin chooses one) and a local
  // object-URL just for the preview avatar.
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // URL once the picked photo has been uploaded (on pick). Preferred over the
  // raw File at submit; the File stays a fallback if the upload didn't finish.
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        name: user?.name || "",
        role: user?.role === "admin" ? "admin" : "employee",
      });
      setImageFile(null);
      setPreviewUrl(null);
      setUploadedUrl(null);
      setUploading(false);
    }
  }, [visible, user, form]);

  // Revoke the object URL when it changes / on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const avatarSrc = previewUrl || user?.image || undefined;

  const beforeUpload = (file) => {
    const isImage = file.type?.startsWith("image/");
    if (!isImage) {
      message.error("Please choose an image file.");
      return Upload.LIST_IGNORE;
    }
    const withinSize = file.size / 1024 / 1024 < MAX_IMAGE_MB;
    if (!withinSize) {
      message.error(`Image must be smaller than ${MAX_IMAGE_MB}MB.`);
      return Upload.LIST_IGNORE;
    }
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    // Upload right away so saving is instant. Keep the File as a fallback in
    // case this doesn't finish (submit re-uploads it then).
    setUploadedUrl(null);
    setUploading(true);
    uploadFile(file, "users")
      .then((url) => {
        if (url) setUploadedUrl(url);
        else message.error("Photo upload failed — we'll retry when you save.");
      })
      .catch(() =>
        message.error("Photo upload failed — we'll retry when you save."),
      )
      .finally(() => setUploading(false));
    // Returning false stops antd from auto-uploading; we handle it ourselves.
    return false;
  };

  const handleFinish = (values) => {
    onSubmit(values, imageFile, uploadedUrl);
  };

  const copyEmail = async () => {
    const email = user?.email;
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      message.success("Email copied");
    } catch (e) {
      message.error("Couldn't copy email");
    }
  };

  return (
    <Modal
      title={<span className="user-details-label">Edit User</span>}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <div className="admin-panel-logout">
        <div className="logout-main-container">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <Avatar
              size={96}
              src={avatarSrc}
              icon={<UserOutlined />}
              style={{ flexShrink: 0 }}
            />
            <Upload
              accept="image/*"
              showUploadList={false}
              maxCount={1}
              beforeUpload={beforeUpload}
            >
              <Button icon={<UploadOutlined />}>Change photo</Button>
            </Upload>
          </div>

          <Form
            form={form}
            name="edit-user-role"
            labelCol={{
              span: 8,
            }}
            wrapperCol={{
              span: 16,
            }}
            onFinish={handleFinish}
            autoComplete="off"
          >
            <Form.Item label="Email">
              <Typography.Text
                style={{ display: "block", width: "100%" }}
                ellipsis={{ tooltip: user?.email || "N/A" }}
              >
                {user?.email || "N/A"}
              </Typography.Text>
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={copyEmail}
                disabled={!user?.email}
                style={{ paddingLeft: 0 }}
              >
                Copy email
              </Button>
            </Form.Item>

            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: "Please enter a name!" }]}
            >
              <Input placeholder="Full name" />
            </Form.Item>

            <Form.Item
              label="Role"
              name="role"
              id="role"
              rules={[
                {
                  required: true,
                  message: "Please select a role!",
                },
              ]}
            >
              <Select
                disabled={lockRole}
                options={[
                  { value: "admin", label: "Admin" },
                  { value: "employee", label: "Employee" },
                ]}
              />
            </Form.Item>

            <Form.Item
              wrapperCol={{
                offset: 8,
                span: 16,
              }}
            >
              <Button disabled={isLoading} type="primary" htmlType="submit">
                {isLoading ? "Loading..." : "Update User"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default EditRoleModal;
